import React, { useCallback } from 'react';
import type { FC } from 'react';
import { css, cssMap } from '@compiled/react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import Avatar from '@atlaskit/avatar/avatar';
import Link from '@atlaskit/link/link';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';
import PageIcon from '@atlaskit/icon/core/page';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import VideoIcon from '@atlaskit/icon/core/video';

import { ActionItem } from './ActionItem.tsx';
import type { Meeting, MeetingActionItem } from './meetingTypes.ts';

const i18n = defineMessages({
	yourActionItems: {
		id: 'feed.meetings.card.your-action-items',
		defaultMessage: 'Your Action Items',
		description: 'Heading above the list of action items for a meeting recording',
	},
	meetingNotes: {
		id: 'feed.meetings.card.meeting-notes',
		defaultMessage: 'Meeting notes',
		description: 'Link to the auto-generated Confluence meeting notes page',
	},
	deepDive: {
		id: 'feed.meetings.card.deep-dive',
		defaultMessage: 'Deep dive',
		description: 'Link that opens the full Loom meeting notes page in Confluence',
	},
	openRecording: {
		id: 'feed.meetings.card.open-recording',
		defaultMessage: 'Open recording for {title}',
		description: 'Accessible label for the button that opens the meeting recording',
	},
	ownedBy: {
		id: 'feed.meetings.card.owned-by',
		defaultMessage: 'Owned by {owner}',
		description: 'Byline showing the owner (recorder) of the meeting recording',
	},
});

const cardStyle = css({
	display: 'flex',
	gap: token('space.150'),
	border: `${token('border.width')} solid ${token('color.border')}`,
	borderRadius: token('radius.large'),
	padding: token('space.200'),
	marginBottom: token('space.200'),
	backgroundColor: token('elevation.surface'),
});

const avatarColumnStyle = css({
	flex: '0 0 auto',
	paddingTop: token('space.025'),
});

const contentColumnStyle = css({
	flex: '1 1 auto',
	minWidth: 0,
});

const headerRowStyle = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: token('space.100'),
});

const notesLinkStyle = cssMap({
	root: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: token('space.050'),
	},
});

const bylineStyle = css({
	marginTop: token('space.050'),
	display: 'flex',
	flexWrap: 'wrap',
	gap: token('space.100'),
	alignItems: 'center',
});

const pressableStyles = cssMap({
	title: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: token('space.075'),
		backgroundColor: token('color.background.neutral.subtle'),
		border: 'none',
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
		cursor: 'pointer',
		font: token('font.heading.small'),
		color: token('color.link'),
		'&:hover': {
			textDecoration: 'underline',
		},
	},
	deepDive: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: token('space.050'),
		flex: '0 0 auto',
		backgroundColor: token('color.background.neutral.subtle'),
		border: 'none',
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
		cursor: 'pointer',
		color: token('color.link'),
		'&:hover': {
			textDecoration: 'underline',
		},
	},
});

const actionItemsHeadingStyle = css({
	marginTop: token('space.150'),
	marginBottom: token('space.050'),
});

type MeetingCardProps = {
	meeting: Meeting;
	/** Opens the embedded Loom recording modal for this meeting. */
	onOpenRecording?: (meeting: Meeting) => void;
	/** Opens the deep-dive (Loom meeting notes page in Confluence). */
	onDeepDive?: (meeting: Meeting) => void;
	/** Invoked when a 1-click action item CTA is clicked. */
	onActionClick?: (meeting: Meeting, item: MeetingActionItem) => void;
};

const formatDate = (iso: string, locale: string): string =>
	new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', year: 'numeric' }).format(
		new Date(iso),
	);

/**
 * A single meeting recording card: title (opens recording), owner + date byline,
 * Meeting notes + Deep dive links, and the action item list.
 */
export const MeetingCard: FC<MeetingCardProps> = ({
	meeting,
	onOpenRecording,
	onDeepDive,
	onActionClick,
}) => {
	const intl = useIntl();

	const handleOpenRecording = useCallback(
		() => onOpenRecording?.(meeting),
		[onOpenRecording, meeting],
	);
	const handleDeepDive = useCallback(() => onDeepDive?.(meeting), [onDeepDive, meeting]);
	const handleActionClick = useCallback(
		(item: MeetingActionItem) => onActionClick?.(meeting, item),
		[onActionClick, meeting],
	);

	return (
		<div css={cardStyle} data-testid="meeting-card">
			<div css={avatarColumnStyle}>
				<Avatar
					size="medium"
					src={meeting.owner.avatarUrl}
					name={meeting.owner.displayName}
					appearance="circle"
				/>
			</div>
			<div css={contentColumnStyle}>
				<div css={headerRowStyle}>
					<Pressable
						xcss={pressableStyles.title}
						onClick={handleOpenRecording}
						aria-label={intl.formatMessage(i18n.openRecording, { title: meeting.title })}
						testId="meeting-card-title"
					>
						<VideoIcon label="" color={token('color.link')} />
						<Text color="color.link" weight="medium">
							{meeting.title}
						</Text>
					</Pressable>
					{meeting.notesPageUrl ? (
						<Pressable
							xcss={pressableStyles.deepDive}
							onClick={handleDeepDive}
							testId="meeting-card-deep-dive"
						>
							<LinkExternalIcon label="" color="currentColor" />
							<Text color="color.link" size="small">
								<FormattedMessage {...i18n.deepDive} />
							</Text>
						</Pressable>
					) : null}
				</div>

				<div css={bylineStyle}>
					<Text color="color.text.subtle" size="small">
						<FormattedMessage
							{...i18n.ownedBy}
							values={{
								owner: (
									<Text as="span" weight="medium" color="color.text.subtle">
										{meeting.owner.displayName}
									</Text>
								),
							}}
						/>
					</Text>
					<Text color="color.text.subtle" size="small" aria-hidden>
						·
					</Text>
					<Text color="color.text.subtle" size="small">
						{formatDate(meeting.startsAt, intl.locale)}
					</Text>
					{meeting.notesPageUrl ? (
						<>
							<Text color="color.text.subtle" size="small" aria-hidden>
								·
							</Text>
							<Box as="span" xcss={notesLinkStyle.root}>
								<PageIcon label="" color={token('color.link')} />
								<Link href={meeting.notesPageUrl} testId="meeting-card-notes-link">
									<FormattedMessage {...i18n.meetingNotes} />
								</Link>
							</Box>
						</>
					) : null}
				</div>

				{meeting.actionItems.length > 0 ? (
					<>
						<div css={actionItemsHeadingStyle}>
							<Text weight="bold" size="small">
								<FormattedMessage {...i18n.yourActionItems} />
							</Text>
						</div>
						{meeting.actionItems.map((item) => (
							<ActionItem key={item.id} item={item} onActionClick={handleActionClick} />
						))}
					</>
				) : null}
			</div>
		</div>
	);
};
